import { createId, nowIso } from '@/modules/db'
import Dexie, { type Table } from 'dexie'

export interface Job {
  id: string
  type: string
  payload: Record<string, unknown>
  status: 'pending' | 'processing' | 'completed' | 'failed'
  attempts: number
  maxAttempts: number
  createdAt: string
  processedAt?: string
  error?: string
}

export class WEFAJobDatabase extends Dexie {
  jobs!: Table<Job>

  constructor() {
    super('wefa-jobs')
    this.version(1).stores({
      jobs: 'id, type, status, createdAt',
    })
  }
}

export const jobDb = new WEFAJobDatabase()

export async function createJob(
  type: string,
  payload: Record<string, unknown>,
  maxAttempts = 5,
): Promise<Job> {
  const job: Job = {
    id: createId(),
    type,
    payload,
    status: 'pending',
    attempts: 0,
    maxAttempts,
    createdAt: nowIso(),
  }
  await jobDb.jobs.add(job)
  return job
}

export async function getNextPendingJob(): Promise<Job | undefined> {
  return jobDb.jobs.where('status').equals('pending').first()
}

export async function markJobProcessing(job: Job): Promise<Job> {
  const nextAttempts = job.attempts + 1
  const updates: Partial<Job> = {
    status: 'processing',
    attempts: nextAttempts,
    error: undefined,
  }
  await jobDb.jobs.update(job.id, updates)
  return { ...job, ...updates, attempts: nextAttempts }
}

export async function markJobCompleted(job: Job): Promise<Job> {
  const updates: Partial<Job> = {
    status: 'completed',
    processedAt: nowIso(),
    error: undefined,
  }
  await jobDb.jobs.update(job.id, updates)
  return { ...job, ...updates }
}

export async function markJobRetryable(job: Job, errorMessage: string): Promise<Job> {
  const updates: Partial<Job> = {
    status: 'pending',
    error: errorMessage,
    processedAt: undefined,
  }
  await jobDb.jobs.update(job.id, updates)
  return { ...job, ...updates }
}

export async function markJobFailed(job: Job, errorMessage: string): Promise<Job> {
  const updates: Partial<Job> = {
    status: 'failed',
    error: errorMessage,
    processedAt: nowIso(),
  }
  await jobDb.jobs.update(job.id, updates)
  return { ...job, ...updates }
}
